import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from '../../components/layout/AdminLayout';
import ComposeBlastModal from '../../components/admin/ComposeBlastModal';
import { Button } from '../../components/ui/button';
import { Megaphone, Calendar, Send, X, Users, Mail, MessageSquare, Clock, CheckCircle2, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AUDIENCE_LABEL = {
  'event': 'Single Event',
  'all-upcoming-events': 'All Upcoming Events',
  'subscribers': 'Subscribers',
  'leads': 'All Leads',
  'resource-downloaders': 'All Resource Downloaders',
  'resource': 'Single Resource',
  'all': 'Everyone',
};

const STATUS_STYLES = {
  scheduled: 'bg-[#EFF6FF] text-[#1D4ED8]',
  running: 'bg-[#FFF7ED] text-[#F97316]',
  sent: 'bg-green-50 text-green-700',
  failed: 'bg-red-50 text-red-700',
  cancelled: 'bg-[#F1F5F9] text-[#475569]',
};

export default function AdminBlasts() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [composeOpen, setComposeOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [list, c] = await Promise.all([
        axios.get(`${API_URL}/api/admin/blasts`, { withCredentials: true, params: statusFilter ? { status: statusFilter } : {} }),
        axios.get(`${API_URL}/api/admin/blasts/audience-counts`, { withCredentials: true }),
      ]);
      setItems(list.data.items || []);
      setCounts(c.data || {});
    } catch (e) {
      toast.error('Failed to load blasts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [statusFilter]);

  const cancel = async (blastId) => {
    if (!window.confirm('Cancel this scheduled blast?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/blasts/${blastId}`, { withCredentials: true });
      toast.success('Cancelled');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to cancel');
    }
  };

  return (
    <AdminLayout title="Blasts">
      <div data-testid="admin-blasts-page">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="heading-2 text-2xl text-[#0A0F1C]" data-testid="blasts-title">Blasts ({items.length})</h1>
            <p className="text-xs text-[#475569] mt-1">
              Send emails or SMS to event registrants, leads, resource downloaders, or everyone - now or scheduled.
            </p>
          </div>
          <Button
            onClick={() => setComposeOpen(true)}
            className="inline-flex items-center gap-2 bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2 rounded-lg text-sm font-medium"
            data-testid="compose-btn"
          >
            <Megaphone size={16} /> New Blast
          </Button>
        </div>

        {/* Audience snapshot */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <StatCard icon={<Calendar size={18} />} label="Upcoming Event Regs" value={counts['all-upcoming-events'] ?? 0} tone="blue" />
          <StatCard icon={<Users size={18} />} label="Subscribers" value={counts['subscribers'] ?? 0} tone="green" />
          <StatCard icon={<Users size={18} />} label="Leads" value={counts['leads'] ?? 0} tone="orange" />
          <StatCard icon={<Mail size={18} />} label="Resource Downloaders" value={counts['resource-downloaders'] ?? 0} tone="green" />
          <StatCard icon={<Megaphone size={18} />} label="Everyone (deduped)" value={counts['all'] ?? 0} tone="violet" />
        </div>

        {/* Status filter */}
        <div className="bg-white p-3 border border-[#E2E8F0] rounded-lg mb-4 flex flex-wrap gap-2">
          {['', 'scheduled', 'sent', 'failed', 'cancelled'].map((s) => (
            <button
              key={s || 'all'}
              onClick={() => setStatusFilter(s)}
              className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${
                statusFilter === s
                  ? 'bg-[#1D4ED8] text-white'
                  : 'bg-[#F8FAFC] text-[#475569] hover:bg-[#EFF6FF]'
              }`}
              data-testid={`filter-${s || 'all'}`}
            >
              {s ? s.charAt(0).toUpperCase() + s.slice(1) : 'All'}
            </button>
          ))}
        </div>

        {/* List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-10 h-10 border-4 border-[#1D4ED8] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-[#E2E8F0] rounded-lg p-8 text-center" data-testid="blasts-empty">
            <Megaphone size={48} className="text-[#E2E8F0] mx-auto mb-4" />
            <p className="text-[#475569]">No blasts yet.</p>
            <p className="text-xs text-[#94A3B8] mt-1">Click "New Blast" to compose your first one.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((b, i) => (
              <div key={b.blast_id} className="bg-white border border-[#E2E8F0] rounded-lg p-4" data-testid={`blast-row-${i}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={`text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_STYLES[b.status] || 'bg-[#F1F5F9] text-[#475569]'}`}>
                        {b.status}
                      </span>
                      <span className="text-xs text-[#475569]">
                        {AUDIENCE_LABEL[b.audience_kind] || b.audience_kind}
                      </span>
                      {b.channels?.includes('email') && (
                        <span className="text-[10px] flex items-center gap-1 text-[#1D4ED8]"><Mail size={10} /> Email</span>
                      )}
                      {b.channels?.includes('sms') && (
                        <span className="text-[10px] flex items-center gap-1 text-[#1D4ED8]"><MessageSquare size={10} /> SMS</span>
                      )}
                    </div>
                    {b.subject && <div className="text-sm font-bold text-[#0A0F1C]">{b.subject}</div>}
                    <div className="text-sm text-[#475569] line-clamp-2">{b.message}</div>
                    <div className="flex items-center gap-3 text-[11px] text-[#94A3B8] mt-2 flex-wrap">
                      {b.status === 'scheduled' && (
                        <span className="flex items-center gap-1 text-[#F97316] font-medium">
                          <Clock size={11} /> Scheduled: {new Date(b.scheduled_at).toLocaleString()}
                        </span>
                      )}
                      {b.status === 'sent' && b.stats && (
                        <>
                          <span className="flex items-center gap-1 text-green-700"><CheckCircle2 size={11} /> Sent</span>
                          <span>email: {b.stats.email || 0}</span>
                          <span>sms: {b.stats.sms || 0}</span>
                          <span>skipped: {b.stats.skipped || 0}</span>
                          <span>failed: {b.stats.failed || 0}</span>
                        </>
                      )}
                      {b.status === 'failed' && b.error && (
                        <span className="flex items-center gap-1 text-red-700"><AlertCircle size={11} /> {b.error}</span>
                      )}
                      <span>created: {new Date(b.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                  {b.status === 'scheduled' && (
                    <button
                      onClick={() => cancel(b.blast_id)}
                      className="text-xs text-[#EF4444] hover:bg-red-50 px-2 py-1 rounded flex items-center gap-1"
                      data-testid={`cancel-blast-${i}`}
                    >
                      <X size={12} /> Cancel
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {composeOpen && (
          <ComposeBlastModal onClose={() => { setComposeOpen(false); load(); }} />
        )}
      </div>
    </AdminLayout>
  );
}

function StatCard({ icon, label, value, tone }) {
  const tones = {
    blue:   'bg-[#EFF6FF] text-[#1D4ED8]',
    orange: 'bg-[#FFF7ED] text-[#F97316]',
    green:  'bg-green-50 text-green-700',
    violet: 'bg-purple-50 text-purple-700',
  };
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-lg p-4">
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tones[tone] || tones.blue}`}>{icon}</div>
        <div>
          <div className="text-[10px] text-[#475569] uppercase tracking-wider font-bold">{label}</div>
          <div className="text-xl font-bold text-[#0A0F1C]">{value}</div>
        </div>
      </div>
    </div>
  );
}
